import {announce} from '@github-ui/aria-live'
import {useCopyToClipboard} from '@github-ui/copy-to-clipboard/useCopyToClipboard'
import {CheckIcon, CopyIcon, KebabHorizontalIcon, PencilIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {type RefObject, useEffect, useRef, useState} from 'react'

import type {ScribeArtifactVersion} from '../types'
import {ArtifactHistoryMenu} from './ArtifactHistoryMenu'
import type {PanelArtifact} from './ArtifactPanelContent'
import type {EditableArtifact} from './create-artifact-dialog-types'

type ArtifactActionsMenuProps = {
  artifact: PanelArtifact | null
  onEdit?: (artifact: EditableArtifact, returnFocusRef: RefObject<HTMLElement | null>) => void
  selectedVersion: ScribeArtifactVersion | null
  onSelectVersion: (version: ScribeArtifactVersion) => void
  onOpenChange?: (open: boolean) => void
}

export function ArtifactActionsMenu({
  artifact,
  onEdit,
  selectedVersion,
  onSelectVersion,
  onOpenChange,
}: ArtifactActionsMenuProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  useEffect(() => () => onOpenChange?.(false), [onOpenChange])
  const {copied, copyToClipboard} = useCopyToClipboard({
    textToCopy: artifact?.body ?? '',
    onCopy: () => announce('Copied!'),
  })
  const number = artifact?.number
  const canEdit = onEdit && number !== undefined
  const canViewHistory =
    artifact?.number !== undefined &&
    artifact.resourceType !== undefined &&
    artifact.resourceId !== undefined &&
    artifact.hasEdits === true

  if (!artifact || (!artifact.body && !canEdit && !canViewHistory)) return null

  return (
    <ActionMenu
      anchorRef={anchorRef}
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen)
        onOpenChange?.(nextOpen)
      }}
    >
      <ActionMenu.Anchor>
        <IconButton
          ref={anchorRef}
          icon={copied ? CheckIcon : KebabHorizontalIcon}
          variant="invisible"
          aria-label="Artifact actions"
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="end">
        <ActionList>
          {onEdit && number !== undefined && (
            <ActionList.Item
              onSelect={() =>
                onEdit(
                  {
                    number,
                    type: artifact.type,
                    name: artifact.name,
                    body: artifact.body,
                  },
                  anchorRef,
                )
              }
            >
              <ActionList.LeadingVisual>
                <PencilIcon />
              </ActionList.LeadingVisual>
              Edit artifact
            </ActionList.Item>
          )}
          {artifact.body && (
            <ActionList.Item onSelect={copyToClipboard}>
              <ActionList.LeadingVisual>
                <CopyIcon />
              </ActionList.LeadingVisual>
              Copy artifact content
            </ActionList.Item>
          )}
          <ArtifactHistoryMenu artifact={artifact} selectedVersion={selectedVersion} onSelect={onSelectVersion} />
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
